import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import { useCallback, useEffect, useRef, useState } from 'react';

import type { MessagingTyper } from '~hooks/useMessaging/models';
import { MessagingChannel } from '~hooks/useMessaging/models';
import MessageSendRow from '~pages/Messages/components/MessageSendRow';
import MessagingTypingIndicator from '~pages/Messages/components/MessagingTypingIndicator';
import { isMessageTooLong } from '~pages/Messages/messagingContentLimit';
import { useMessagingDraftContent } from '~pages/Messages/MessagingDraftContext';
import { useMessagingOutbox } from '~pages/Messages/MessagingOutboxContext';
import { Nullable } from '~types/Nullable';

import TargetMessagingComposerNote from './TargetMessagingComposerNote';

export type MessageStarter = {
  labelKey: string;
  messageKey: string;
};

type FirstMessageComposerProps = {
  composerNote?: string;
  draftKey: string;
  existingChannel: Nullable<MessagingChannel>;
  hint: string;
  inlineNoticeFor?: (error: unknown) => Nullable<string>;
  onTyping?: () => void;
  openChannel: () => Promise<MessagingChannel>;
  starters?: MessageStarter[];
  typers: MessagingTyper[];
};

export default function FirstMessageComposer({
  composerNote,
  draftKey,
  existingChannel,
  hint,
  inlineNoticeFor,
  onTyping,
  openChannel,
  starters = [],
  typers,
}: FirstMessageComposerProps) {
  const theme = useTheme();
  const { sendThroughOutbox } = useMessagingOutbox();

  const inputRef = useRef<HTMLTextAreaElement>(null);
  const {
    changeContent,
    clearContent,
    content,
    hideDraftWhileSending,
    restoreUnsentContent,
  } = useMessagingDraftContent(draftKey);
  const [isOpeningChannel, setIsOpeningChannel] = useState(false);
  const [inlineNotice, setInlineNotice] = useState<Nullable<string>>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const draft = content.trim();
  const canSend = draft.length > 0 && !isMessageTooLong(content) && !isOpeningChannel;

  const send = useCallback(async () => {
    if (!canSend) return;

    setIsOpeningChannel(true);
    setInlineNotice(null);
    // Opening the channel can swap in the thread composer before it resolves, and that composer must not inherit this message.
    hideDraftWhileSending();

    try {
      const targetChannel = existingChannel ?? (await openChannel());

      clearContent();
      void sendThroughOutbox({ attachments: [], channel: targetChannel, content: draft });
    } catch (error) {
      // Anything without an inline notice was toasted by the hook; the draft stays for another try.
      restoreUnsentContent(content);
      setInlineNotice(inlineNoticeFor?.(error) ?? null);
    } finally {
      setIsOpeningChannel(false);
    }
  }, [
    canSend,
    clearContent,
    content,
    draft,
    existingChannel,
    inlineNoticeFor,
    openChannel,
    hideDraftWhileSending,
    restoreUnsentContent,
    sendThroughOutbox,
  ]);

  const fillStarter = useCallback(
    (messageKey: string) => {
      changeContent(t(messageKey));
      inputRef.current?.focus();
    },
    [changeContent],
  );

  const handleContentChange = useCallback(
    (value: string) => {
      changeContent(value);
      if (value.trim().length > 0) onTyping?.();
    },
    [changeContent, onTyping],
  );

  return (
    <Box sx={{ display: 'flex', flex: 1, flexDirection: 'column', minHeight: 0 }}>
      <Box
        sx={{
          alignItems: 'center',
          display: 'flex',
          flex: 1,
          flexDirection: 'column',
          gap: 1.5,
          justifyContent: 'center',
          px: 3,
          textAlign: 'center',
        }}
      >
        <Typography sx={{ color: theme.semantic.textSecondary }} variant="body2">
          {hint}
        </Typography>

        {starters.length > 0 && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              maxWidth: 480,
              width: '100%',
            }}
          >
            {starters.map((starter) => (
              <ButtonBase
                key={starter.labelKey}
                onClick={() => fillStarter(starter.messageKey)}
                sx={{
                  backgroundColor: theme.semantic.chipBrandBg,
                  border: `1px solid ${theme.semantic.chipBrandBorder}`,
                  borderRadius: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  gap: 0.25,
                  px: 1.5,
                  py: 1,
                  textAlign: 'left',
                  width: '100%',
                }}
              >
                <Typography
                  sx={{ color: theme.semantic.chipBrandText, fontWeight: 600 }}
                  variant="body2"
                >
                  {t(starter.labelKey)}
                </Typography>
                <Typography sx={{ color: theme.semantic.textSecondary }} variant="body2">
                  {t(starter.messageKey)}
                </Typography>
              </ButtonBase>
            ))}
          </Box>
        )}
      </Box>

      <Box
        data-test-id="first-message-composer"
        sx={{
          backgroundColor: theme.semantic.surface,
          borderTop: `1px solid ${theme.semantic.divider}`,
          display: 'flex',
          flexDirection: 'column',
          flexShrink: 0,
          gap: 1,
          p: 2,
        }}
      >
        {inlineNotice && (
          <Typography
            role="alert"
            sx={{ color: theme.brandV2.colors.treadRed, fontSize: '12px', px: 0.5 }}
          >
            {inlineNotice}
          </Typography>
        )}

        {composerNote && <TargetMessagingComposerNote note={composerNote} />}

        <MessagingTypingIndicator typers={typers} />

        <MessageSendRow
          canSend={canSend}
          content={content}
          inputRef={inputRef}
          isSending={isOpeningChannel}
          onContentChange={handleContentChange}
          onSend={() => void send()}
        />
      </Box>
    </Box>
  );
}
